/**
 * F11 — Admin venue-claim review queue.
 *
 * Route: /venue-claims
 * Permission: venue_claims:review
 *
 * FIFO queue of submitted venue claims. Status-chip filters above the table
 * (Pending / Approved / Rejected / Withdrawn / Revoked / All). Cursor-based
 * pagination, 25 rows per page. Empty / loading / error states.
 *
 * The page itself does NOT poll for the sidenav badge — that lives in
 * `NavCountsProvider` and is invoked once per app shell.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useI18n } from '@bravemove/i18n/react';
import {
  Banner,
  Chip,
  EmptyState,
  Skeleton,
  StatusChip,
  type StatusChipStatus,
  Table,
  type TableColumn,
} from '@bravemove/ui-web';
import { UIIcon } from '@bravemove/design-tokens/icons';
import type { AdminPageMeta, VenueClaimEntityStatus } from '@bravemove/types';
import {
  adminVenueClaimApi,
  type VenueClaimQueueOpts,
  type VenueClaimQueueRow,
} from '../api/venue-claim.js';
import { translateApiError } from '../auth/errors.js';
import { PageHeader } from '../components/PageHeader';
import { AdminPagination } from '../components/AdminPagination';

const PAGE_LIMIT = 25;
type StatusFilter = VenueClaimEntityStatus | 'all';
const DEFAULT_STATUS: StatusFilter = 'pending';

const FILTER_CHIPS: ReadonlyArray<{ value: StatusFilter; labelKey: string }> = [
  { value: 'pending', labelKey: 'admin:claim.queue.filter_status_pending' },
  { value: 'approved', labelKey: 'admin:claim.queue.filter_status_approved' },
  { value: 'rejected', labelKey: 'admin:claim.queue.filter_status_rejected' },
  { value: 'withdrawn', labelKey: 'admin:claim.queue.filter_status_withdrawn' },
  { value: 'revoked_by_admin', labelKey: 'admin:claim.queue.filter_status_revoked' },
  { value: 'all', labelKey: 'admin:claim.queue.filter_status_all' },
];

const STATUS_CHIP_MAP: Record<VenueClaimEntityStatus, StatusChipStatus> = {
  draft: 'pending',
  pending: 'in_review',
  approved: 'approved',
  rejected: 'rejected',
  withdrawn: 'pending',
  revoked_by_admin: 'rejected',
};

/** Build a sparse list opts object so undefined keys aren't serialised. */
function buildListOpts(raw: {
  status: StatusFilter;
  page: number;
  venueId: string | null;
}): VenueClaimQueueOpts {
  const opts: Record<string, string | number> = { perPage: PAGE_LIMIT, page: raw.page };
  if (raw.status) opts.status = raw.status;
  // V-11 — the venue-detail Claims tab links here with `?venueId=`; forward it
  // to the server-side filter instead of silently dropping it.
  if (raw.venueId) opts.venueId = raw.venueId;
  return opts as unknown as VenueClaimQueueOpts;
}

function formatRelative(iso: string | null, locale: string): string {
  if (!iso) return '—';
  const target = new Date(iso).getTime();
  if (Number.isNaN(target)) return '—';
  const diffMs = target - Date.now();
  const diffMin = Math.round(diffMs / 60_000);
  try {
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
    const absMin = Math.abs(diffMin);
    if (absMin < 60) return rtf.format(diffMin, 'minute');
    const diffHour = Math.round(diffMin / 60);
    if (Math.abs(diffHour) < 24) return rtf.format(diffHour, 'hour');
    const diffDay = Math.round(diffHour / 24);
    return rtf.format(diffDay, 'day');
  } catch {
    return iso;
  }
}

export function VenueClaimsQueuePage() {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const venueIdParam = searchParams.get('venueId');
  // When narrowed to one venue, default to 'all' (mirrors the backend default)
  // so the venue's full claim history shows, not just pending.
  const statusParam =
    (searchParams.get('status') as StatusFilter | null) ?? (venueIdParam ? 'all' : DEFAULT_STATUS);
  const pageParam = Math.max(Number.parseInt(searchParams.get('page') ?? '1', 10) || 1, 1);

  const [rows, setRows] = useState<readonly VenueClaimQueueRow[]>([]);
  const [pagination, setPagination] = useState<AdminPageMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchList = useCallback(
    async (opts: VenueClaimQueueOpts) => {
      setLoading(true);
      setRows([]);
      setError(null);
      try {
        const data = await adminVenueClaimApi.listQueue(opts);
        setRows(data.items);
        setPagination(data.pagination ?? null);
      } catch (err) {
        setError(translateApiError(err, t));
      } finally {
        setLoading(false);
      }
    },
    [t],
  );

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchList(
      buildListOpts({
        status: statusParam,
        page: pageParam,
        venueId: venueIdParam,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusParam, pageParam, venueIdParam]);

  const onStatusChange = (value: StatusFilter) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value !== DEFAULT_STATUS) next.set('status', value);
      else next.delete('status');
      next.delete('page');
      return next;
    });
  };

  const goToPage = (page: number) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (page <= 1) next.delete('page');
      else next.set('page', String(page));
      return next;
    });
  };

  const columns = useMemo<readonly TableColumn<VenueClaimQueueRow>[]>(
    () => [
      {
        key: 'venue',
        header: t('admin:claim.queue.column_venue'),
        cell: (row) => (
          <div className="flex flex-col gap-0.5">
            <span className="font-medium text-[13px] text-ink">{row.venue?.name ?? '—'}</span>
            {row.venue?.slug ? (
              <span className="font-mono text-[11.5px] text-ink-3">{row.venue.slug}</span>
            ) : null}
          </div>
        ),
      },
      {
        key: 'account',
        header: t('admin:claim.queue.column_account'),
        cell: (row) => (
          <span className="text-[12.5px] text-ink">{row.account?.displayName ?? '—'}</span>
        ),
      },
      {
        key: 'claimant',
        header: t('admin:claim.queue.column_claimant'),
        cell: (row) => (
          <div className="flex flex-col gap-0.5">
            <span className="text-[12.5px] text-ink">{row.claimant?.displayName || '—'}</span>
            {row.claimant?.email ? (
              <span className="font-mono text-[11px] text-ink-3">{row.claimant.email}</span>
            ) : null}
          </div>
        ),
      },
      {
        key: 'status',
        header: t('admin:claim.queue.column_status'),
        cell: (row) => (
          <StatusChip
            size="sm"
            status={STATUS_CHIP_MAP[row.status] ?? 'pending'}
            pulse={row.status === 'pending'}
            label={t(
              `admin:claim.queue.filter_status_${row.status === 'revoked_by_admin' ? 'revoked' : row.status}`,
            )}
          />
        ),
      },
      {
        key: 'submitted',
        header: t('admin:claim.queue.column_submitted'),
        cell: (row) => (
          <span className="text-[12.5px] text-ink-2">
            {formatRelative(row.submittedAt ?? row.createdAt, locale)}
          </span>
        ),
      },
    ],
    [t, locale],
  );

  return (
    <div className="space-y-6">
      <PageHeader title={t('admin:claim.queue.title')} subtitle={t('admin:claim.queue.subtitle')} />

      {/* Venue filter chip (V-11 — set when deep-linked from a venue detail page) */}
      {venueIdParam ? (
        <div className="flex flex-wrap items-center gap-2" data-testid="venue-claims-venue-filter">
          <Chip
            selected
            onClick={() => {
              setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                next.delete('venueId');
                next.delete('page');
                return next;
              });
            }}
          >
            {t('admin:claim.queue.venue_filter', {
              defaultValue: 'Venue: {id} ✕',
              id: rows[0]?.venue?.name ?? venueIdParam,
            })}
          </Chip>
        </div>
      ) : null}

      {/* Status filter chips */}
      <div
        role="tablist"
        aria-label={t('admin:claim.queue.filter_status_label')}
        className="flex flex-wrap items-center gap-2"
      >
        {FILTER_CHIPS.map((chip) => (
          <Chip
            key={chip.value}
            selected={statusParam === chip.value}
            onClick={() => onStatusChange(chip.value)}
          >
            {t(chip.labelKey)}
          </Chip>
        ))}
      </div>

      {error ? <Banner tone="danger">{error}</Banner> : null}

      {loading ? (
        <div className="space-y-3" data-testid="venue-claims-loading">
          {Array.from({ length: 5 }, (_, i) => (
            <Skeleton key={i} className="h-14 w-full rounded-lg" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<UIIcon name="receipt" size={36} color="currentColor" />}
          title={t('admin:claim.queue.empty_title')}
          description={t('admin:claim.queue.empty_body')}
        />
      ) : (
        <Table
          data={rows as VenueClaimQueueRow[]}
          columns={columns as TableColumn<VenueClaimQueueRow>[]}
          rowKey={(row) => row.id}
          onRowPress={(row) => navigate(`/venue-claims/${row.id}`)}
          empty={t('admin:claim.queue.empty_title')}
        />
      )}

      {pagination && !loading && rows.length > 0 ? (
        <AdminPagination
          pagination={pagination}
          shownCount={rows.length}
          onChange={goToPage}
          testId="venue-claims-pagination"
        />
      ) : null}
    </div>
  );
}
